import { create } from "zustand";
import axios from "axios";
import { connectSocket } from "../socket/employeesSocket";
import useAuthStore from "./useAuthStore";

const API_URL = import.meta.env.VITE_API_BASE_URL;
axios.defaults.withCredentials = true;

const NO_GROUP_LABEL = "Bez grupe";

/** null / "" / "Bez grupe" → isti ključ u groupsState (kao u Settings/Calendar). */
const normalizeEmployeeGroupName = (value) => {
    if (value == null || value === "" || value === "null" || value === "undefined" || value === NO_GROUP_LABEL) {
        return NO_GROUP_LABEL;
    }
    return value;
};

const DUPLICATE_REQUEST_WINDOW_MS = 1000;
const REQUEST_DEDUPLICATION_TTL_MS = 60 * 1000;
const inFlightRequests = new Map();
const recentlyCompletedRequests = new Map();

const stableSerialize = (value) => {
    if (value === null || value === undefined) return String(value);
    if (typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) {
        return `[${value.map((item) => stableSerialize(item)).join(",")}]`;
    }

    const keys = Object.keys(value).sort();
    const serializedEntries = keys.map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`);
    return `{${serializedEntries.join(",")}}`;
};

const buildRequestKey = ({ method, url, payload }) => {
    return `${method}:${url}:${stableSerialize(payload)}`;
};

const cleanupRequestCaches = () => {
    const now = Date.now();
    for (const [key, meta] of recentlyCompletedRequests.entries()) {
        if (now - meta.completedAt > REQUEST_DEDUPLICATION_TTL_MS) {
            recentlyCompletedRequests.delete(key);
        }
    }
};

const runDedupedRequest = ({ method, url, payload, requestFn, cooldownMs = DUPLICATE_REQUEST_WINDOW_MS }) => {
    const requestKey = buildRequestKey({ method, url, payload });
    const inFlightPromise = inFlightRequests.get(requestKey);
    if (inFlightPromise) {
        return inFlightPromise;
    }

    const recentCall = recentlyCompletedRequests.get(requestKey);
    const now = Date.now();
    if (recentCall && now - recentCall.completedAt <= cooldownMs) {
        return Promise.resolve(recentCall.result);
    }

    const guardedPromise = Promise.resolve()
        .then(requestFn)
        .then((result) => {
            recentlyCompletedRequests.set(requestKey, {
                completedAt: Date.now(),
                result
            });
            cleanupRequestCaches();
            return result;
        })
        .finally(() => {
            inFlightRequests.delete(requestKey);
        });

    inFlightRequests.set(requestKey, guardedPromise);
    return guardedPromise;
};

const apiPost = (url, payload, config, options = {}) => {
    return runDedupedRequest({
        method: "POST",
        url,
        payload,
        cooldownMs: options.cooldownMs,
        requestFn: async () => {
            const { data } = await axios.post(url, payload, config);
            return data;
        }
    });
};

const apiGet = (url, config, options = {}) => {
    const payload = config?.params || null;
    return runDedupedRequest({
        method: "GET",
        url,
        payload,
        cooldownMs: options.cooldownMs,
        requestFn: async () => {
            const { data } = await axios.get(url, config);
            return data;
        }
    });
};

const getMonthKey = (month, year) => `${year}-${String(month + 1).padStart(2, "0")}`;

const formatKarnetDayCell = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return {
        formatted: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
        round30min: (Math.round(minutes / 30) * 30) / 60,
        round1h: Math.round(minutes / 60)
    };
};

const patchKarnetUserFields = (data, userKey, fields) => {
    if (!data?.[userKey]) return data;

    return {
        ...data,
        [userKey]: {
            ...data[userKey],
            ...fields,
        },
    };
};

const patchStatsMonthlyCacheForEmployee = (cache, userKey, fields) => {
    if (!cache) return cache;

    let changed = false;
    const nextCache = { ...cache };

    Object.entries(nextCache).forEach(([monthKey, monthData]) => {
        if (!monthData?.karnetData?.[userKey]) return;

        const nextMonthData = { ...monthData };
        nextMonthData.karnetData = patchKarnetUserFields(monthData.karnetData, userKey, fields);

        if (monthData.filteredKarnetData?.[userKey]) {
            nextMonthData.filteredKarnetData = patchKarnetUserFields(
                monthData.filteredKarnetData,
                userKey,
                fields,
            );
        }

        nextCache[monthKey] = nextMonthData;
        changed = true;
    });

    return changed ? nextCache : cache;
};

const patchKarnetUserDay = (karnetData, filteredKarnetData, userId, dayKey, workedMinutes) => {
    const userKey = String(userId);
    const user = karnetData?.[userKey];
    if (!user?.days) return null;

    const nextKarnetData = {
        ...karnetData,
        [userKey]: {
            ...user,
            days: {
                ...user.days,
                [dayKey]: workedMinutes
            }
        }
    };

    const nextFiltered = {
        ...filteredKarnetData,
        [userKey]: {
            ...(filteredKarnetData?.[userKey] || user),
            days: {
                ...(filteredKarnetData?.[userKey]?.days || {}),
                [dayKey]: formatKarnetDayCell(workedMinutes)
            }
        }
    };

    return { karnetData: nextKarnetData, filteredKarnetData: nextFiltered };
};

const patchEmployeeMonthLogsByDay = (logsByDay, { year, month, day, logId, newTime }) => {
    const yearStr = String(year);
    const monthStr = String(month);
    const dayStr = String(day);
    const keys = [
        `${yearStr}-${monthStr}-${dayStr}`,
        `${yearStr}-${monthStr.padStart(2, "0")}-${dayStr.padStart(2, "0")}`
    ];

    for (const key of keys) {
        const dayLogs = logsByDay[key];
        if (!Array.isArray(dayLogs)) continue;

        const logIndex = dayLogs.findIndex((log) => String(log._id) === String(logId));
        if (logIndex === -1) continue;

        return {
            ...logsByDay,
            [key]: dayLogs.map((log, index) =>
                index === logIndex ? { ...log, vreme: newTime } : log
            )
        };
    }

    return logsByDay;
};

const monthFilterMatchesPayload = (filter, month0, yearNum) =>
    filter &&
    Number(filter.year) === yearNum &&
    Number(filter.month) === month0;

const getDefaultAppState = () => ({
    loading: true,
    error: null,
    activeSection: 'Prisutnost',
    isSidebarExpanded: false,
    employeesData: null,
    employeesState: {},
    groupsState: {},
    presenceVisibility: 'currentActive',
    presenceNotifications: {},
    devicesData: null,
    deviceDataSignal: false,
    devicesState: {},
    deviceGroupsState: {},
    deviceGroupMovedClients: [],
    employeeGroupMovedUserIds: [],
    deviceUpdateSuccessToast: false,
    employeeDayLogTimeUpdateSuccessToast: false,
    employeeDeleteSuccessToast: false,
    employeeUpdateSuccessToast: false,
    employeeCreateSuccessToast: false,
    absenceRequestSuccessToast: false,
    workCheckInToast: null,
    checkInDeviceRooms: null,
    loadingCheckInDeviceRooms: false,
    lastFetchedCheckInDeviceRoomsAt: null,
    hasHR: false,
    notifications: [],
    unreadNotificationCount: 0,
    isNotificationModalOpen: false,
    devicesVisibility: 'allDevices',
    karnetData: null,
    karnetFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },
    filteredKarnetData: {},
    karnetRoundingInterval: 'round30min',
    employeeMonthLogsByDay: null,
    loadingEmployeeMonthLogs: false,
    employeeMonthLogsModalUserId: null,
    employeeMonthLogsModalDateFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },
    loadingKarnetData: false,
    karnetRowFlashUserId: null,
    karnetRowFlashTick: 0,
    statsRowFlashUserId: null,
    statsRowFlashTick: 0,
    calendarData: null,
    calendarVisibility: 'calendarMonth',
    loadingCalendarData: false,
    selectedCalendarRequestId: null,
    hoveredCalendarRequestId: null,
    isWorkPresenceFormOpen: false,
    isEmployeeFormOpen: false,
    scannedUnknownTagId: null,
    scannedUnknownTagTick: 0,
    selectedShiftConfirmationNotificationId: null,
    calendarFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
        firstDisplayedDate: null,
        lastDisplayedDate: null,
    },
    statsFilter: {
        group: 'Sve grupe',
    },
    statsDateFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },
    statsMonthlyCache: {},
    userStatsDateFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },
    userStatsMonthLogsByDay: null,
    loadingUserStatsMonthLogs: false,
    statsMonthReportModalOpen: false,
    loadingStatsMonthReport: false,
    statsMonthReportData: null,
    statsMonthReportDateFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },
});

export default create((set, get) => ({
    loading: true,  // Dok ne stignu pocetni podaci init

    error: null,

    activeSection: 'Prisutnost',

    isSidebarExpanded: false,

    employeesData: null,

    employeesState: {},

    groupsState: {},

    presenceVisibility: 'currentActive', // 'allEmployees', 'todayActive'

    presenceNotifications: {},

    devicesData: null,

    deviceDataSignal: false,

    devicesState: {},

    deviceGroupsState: {},

    deviceGroupMovedClients: [],

    employeeGroupMovedUserIds: [],

    deviceUpdateSuccessToast: false,
    employeeDayLogTimeUpdateSuccessToast: false,
    employeeDeleteSuccessToast: false,
    employeeUpdateSuccessToast: false,
    employeeCreateSuccessToast: false,
    absenceRequestSuccessToast: false, // može biti false ili string: "podnošenje", "izmena", "potvrda"
    workCheckInToast: null,

    resetAppState: () => {
        // Resetujemo samo session-scoped podatke; metode store-a ostaju netaknute.
        set(getDefaultAppState());
    },

    setDeviceUpdateSuccessToast: (show) => {
        set({ deviceUpdateSuccessToast: show });
    },

    clearDeviceGroupMoveAnimation: (client) => {
        set((state) => ({
            deviceGroupMovedClients: state.deviceGroupMovedClients.filter((c) => c !== client),
        }));
    },

    clearEmployeeGroupMoveAnimation: (userId) => {
        set((state) => ({
            employeeGroupMovedUserIds: state.employeeGroupMovedUserIds.filter(
                (id) => String(id) !== String(userId)
            ),
        }));
    },

    setEmployeeDayLogTimeUpdateSuccessToast: (show) => {
        set({ employeeDayLogTimeUpdateSuccessToast: show });
    },

    setEmployeeDeleteSuccessToast: (show) => {
        set({ employeeDeleteSuccessToast: show });
    },

    setEmployeeUpdateSuccessToast: (show) => {
        set({ employeeUpdateSuccessToast: show });
    },

    setEmployeeCreateSuccessToast: (show) => {
        set({ employeeCreateSuccessToast: show });
    },

    setAbsenceRequestSuccessToast: (actionType) => {
        set({ absenceRequestSuccessToast: actionType });
    },

    setWorkCheckInToast: (toastData) => {
        set({ workCheckInToast: toastData });
    },

    checkInDeviceRooms: null,
    loadingCheckInDeviceRooms: false,
    lastFetchedCheckInDeviceRoomsAt: null,

    setCheckInDeviceRooms: async (forceRefresh = false) => {
        const { checkInDeviceRooms, lastFetchedCheckInDeviceRoomsAt } = get();
        const ttlMs = 5 * 60 * 1000; // 5 min
        const isFresh = lastFetchedCheckInDeviceRoomsAt && (Date.now() - lastFetchedCheckInDeviceRoomsAt < ttlMs);

        if (!forceRefresh && checkInDeviceRooms && isFresh) {
            return checkInDeviceRooms;
        }

        set({ loadingCheckInDeviceRooms: true });
        try {
            const data = await apiPost(`${API_URL}/api/app/checkInDeviceRooms`);
            const rooms = Array.isArray(data) ? data : [];
            console.log("rooms", rooms);
            set({
                checkInDeviceRooms: rooms,
                lastFetchedCheckInDeviceRoomsAt: Date.now()
            });
            return rooms;
        } catch (error) {
            console.error('setCheckInDeviceRooms error:', error);
            return null;
        } finally {
            set({ loadingCheckInDeviceRooms: false });
        }
    },

    hasHR: false,
    notifications: [],
    unreadNotificationCount: 0,
    isNotificationModalOpen: false,

    setNotifications: (notifications) => {
        const unreadCount = notifications.filter(n => !n.read).length;
        set({
            notifications,
            unreadNotificationCount: unreadCount
        });
    },

    addNotification: (notification) => {
        set((state) => {
            // Proveri da li notifikacija već postoji (po _id)
            const exists = state.notifications.some(n => n._id === notification._id);
            if (exists) {
                return state; // Ne dodavaj duplikat
            }

            const newNotifications = [notification, ...state.notifications];
            const unreadCount = newNotifications.filter(n => !n.read).length;

            // Kada stigne notifikacija za prijavu traženog zaposlenog, izbaci ga iz presenceNotifications
            const updates = {
                notifications: newNotifications,
                unreadNotificationCount: unreadCount
            };
            if (notification.type === 'employee_login' && notification.relatedUserId) {
                if (state.presenceNotifications[notification.relatedUserId]) {
                    updates.presenceNotifications = {
                        ...state.presenceNotifications,
                        [notification.relatedUserId]: false
                    };
                }
            }
            return updates;
        });
    },

    setIsNotificationModalOpen: (isOpen) => {
        set({ isNotificationModalOpen: isOpen });
    },

    markNotificationAsRead: async (notificationId) => {
        try {
            const data = await apiPost(`${API_URL}/api/app/notification/markAsRead`, {
                notificationId
            });

            if (data?.ok) {
                set((state) => {
                    const updatedNotifications = state.notifications.map((notification) => (
                        notification._id === notificationId
                            ? { ...notification, read: true }
                            : notification
                    ));

                    const unreadCount = updatedNotifications.filter(n => !n.read).length;

                    return {
                        notifications: updatedNotifications,
                        unreadNotificationCount: unreadCount
                    };
                });
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    },

    markAllNotificationsAsRead: async () => {
        try {
            const data = await apiPost(`${API_URL}/api/app/notification/markAllAsRead`);

            if (data?.ok) {
                set((state) => ({
                    notifications: state.notifications.map((notification) => ({
                        ...notification,
                        read: true
                    })),
                    unreadNotificationCount: 0
                }));
            }
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    },

    devicesVisibility: 'allDevices', // 'statusOn', 'statusOff'

    karnetData: null,

    karnetFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },

    filteredKarnetData: {},

    karnetRoundingInterval: 'round30min', //'round1h' 

    employeeMonthLogsByDay: null,
    loadingEmployeeMonthLogs: false,
    employeeMonthLogsModalUserId: null,
    /** Mesec/godina samo za modal jednog zaposlenog; pri otvaranju kopira statsDateFilter, ne menja statistiku. */
    employeeMonthLogsModalDateFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },

    loadingKarnetData: false,

    /** Karnet / statistika red flash — tick se uvećava pri socket patch-u istog meseca. */
    karnetRowFlashUserId: null,
    karnetRowFlashTick: 0,
    statsRowFlashUserId: null,
    statsRowFlashTick: 0,

    calendarData: null,

    calendarVisibility: 'calendarMonth', // 'calendarMonth', 'calendarTable'
    setCalendarVisibility: (visibility) => {
        set({ calendarVisibility: visibility })
    },

    loadingCalendarData: false,

    selectedCalendarRequestId: null,

    hoveredCalendarRequestId: null,

    setSelectedCalendarRequestId: (requestId) => {
        set({ selectedCalendarRequestId: requestId })
    },

    setHoveredCalendarRequestId: (requestId) => {
        set({ hoveredCalendarRequestId: requestId })
    },

    isWorkPresenceFormOpen: false,

    setIsWorkPresenceFormOpen: (isOpen) => {
        set({ isWorkPresenceFormOpen: isOpen })
        if (isOpen) {
            get().setCheckInDeviceRooms();
        }
    },

    isEmployeeFormOpen: false,
    scannedUnknownTagId: null,
    scannedUnknownTagTick: 0,

    setIsEmployeeFormOpen: (isOpen) => {
        set({
            isEmployeeFormOpen: isOpen,
            ...(isOpen ? {} : { scannedUnknownTagId: null }),
        });
    },

    handleUnknownRfidTag: (payload) => {
        const tagId = payload?.tagId != null ? String(payload.tagId).trim() : '';
        if (!tagId || !get().isEmployeeFormOpen) return;

        set((state) => ({
            scannedUnknownTagId: tagId,
            scannedUnknownTagTick: (state.scannedUnknownTagTick ?? 0) + 1,
        }));
    },

    selectedShiftConfirmationNotificationId: null,

    setSelectedShiftConfirmationNotificationId: (notificationId) => {
        set({ selectedShiftConfirmationNotificationId: notificationId })
    },

    confirmShiftConfirmationRequest: async ({ notificationId, attendanceLogId }) => {
        try {
            const data = await apiPost(`${API_URL}/api/presence/confirm-shift-request`, {
                notificationId,
                attendanceLogId
            });
            if (data?.ok) {
                set({
                    workCheckInToast: {
                        type: "success",
                        title: "Prijava potvrđena",
                        message: "Uspešno ste potvrdili prijavu zaposlenog."
                    }
                });
            }
            return data;
        } catch (error) {
            console.error('confirmShiftConfirmationRequest error:', error);
            return null;
        }
    },

    calendarFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
        firstDisplayedDate: null,
        lastDisplayedDate: null,
    },

    statsFilter: {
        group: "Sve grupe",
    },

    statsDateFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },

    // key: YYYY-MM
    statsMonthlyCache: {},

    userStatsDateFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },

    userStatsMonthLogsByDay: null,

    loadingUserStatsMonthLogs: false,

    statsMonthReportModalOpen: false,
    loadingStatsMonthReport: false,
    statsMonthReportData: null,
    statsMonthReportDateFilter: {
        year: new Date().getFullYear(),
        month: new Date().getMonth(),
    },

    setStatsMonthlyCache: (month, year, payload) => {
        const key = getMonthKey(month, year);
        set((state) => ({
            statsMonthlyCache: {
                ...state.statsMonthlyCache,
                [key]: {
                    ...(state.statsMonthlyCache[key] || {
                        karnetData: null,
                        filteredKarnetData: null,
                        calendarData: null,
                        karnetLoaded: false,
                        calendarLoaded: false,
                        lastFetchedAt: null
                    }),
                    ...payload
                }
            }
        }));
    },

    setStatsFilterGroup: (group) => {
        set((state) => ({
            statsFilter: {
                ...state.statsFilter,
                group
            }
        }));
    },

    setStatsDateFilterMonth: (month) => {
        set((state) => ({
            statsDateFilter: {
                ...state.statsDateFilter,
                month
            }
        }));
    },

    setStatsDateFilterYear: (year) => {
        set((state) => ({
            statsDateFilter: {
                ...state.statsDateFilter,
                year
            }
        }));
    },

    setUserStatsDateFilterMonth: (month) => {
        set((state) => ({
            userStatsDateFilter: {
                ...state.userStatsDateFilter,
                month
            }
        }));
    },

    setUserStatsDateFilterYear: (year) => {
        set((state) => ({
            userStatsDateFilter: {
                ...state.userStatsDateFilter,
                year
            }
        }));
    },

    loadUserStatsMonthLogsData: async (month, year) => {
        set({ loadingUserStatsMonthLogs: true, userStatsMonthLogsByDay: null });
        try {
            const data = await apiPost(`${API_URL}/api/app/user/employeeMonthLogs`, {
                month: month + 1,
                year
            });
            set({
                userStatsMonthLogsByDay: data ?? null,
                loadingUserStatsMonthLogs: false
            });
        } catch (error) {
            console.error("Error fetching user stats month logs:", error);
            set({ userStatsMonthLogsByDay: null, loadingUserStatsMonthLogs: false });
        }
    },

    ensureUserStatsMonthData: async (month, year) => {
        await get().loadUserStatsMonthLogsData(month, year);
    },

    ensureStatsMonthData: async (month, year) => {
        await Promise.all([
            get().setKarnetData(month, year),
            get().setCalendarData(month, year)
        ]);
    },

    setCalendarData: async (month, year) => {
        const key = getMonthKey(month, year);
        const cachedMonth = get().statsMonthlyCache[key];

        const firstDayOfMonth = new Date(year, month, 1);
        let firstDayWeekday = firstDayOfMonth.getDay();
        firstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;
        const firstDisplayedDate = new Date(year, month, 1);
        firstDisplayedDate.setDate(firstDisplayedDate.getDate() - firstDayWeekday);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const daysInMonth = lastDayOfMonth.getDate();
        const totalCells = (firstDayWeekday + daysInMonth > 35) ? 42 : 35;
        const lastDisplayedDate = new Date(firstDisplayedDate);
        lastDisplayedDate.setDate(lastDisplayedDate.getDate() + totalCells - 1);

        const newFilter = {
            year,
            month,
            firstDisplayedDate: firstDisplayedDate.getTime(),
            lastDisplayedDate: lastDisplayedDate.getTime(),
        };

        set({
            calendarFilter: newFilter,
            loadingCalendarData: true
        });

        if (cachedMonth?.calendarLoaded && Array.isArray(cachedMonth.calendarData)) {
            set({
                calendarFilter: newFilter,
                calendarData: cachedMonth.calendarData,
                loadingCalendarData: false
            });
            return;
        }

        const data = await apiPost(`${API_URL}/api/app/calendarAbsences`, {
            month: month + 1,
            year: year,
        });

        set({
            calendarFilter: newFilter,
            calendarData: data,
            loadingCalendarData: false
        });

        get().setStatsMonthlyCache(month, year, {
            calendarData: data,
            calendarLoaded: true,
            lastFetchedAt: Date.now()
        });
    },

    // Preko socketa
    handleNewAbsence: (absence) => {
        const { calendarFilter, calendarData, statsMonthlyCache } = get();
        const { firstDisplayedDate, lastDisplayedDate, month, year } = calendarFilter;

        const startAbsence = new Date(absence.godina_pocetak, absence.mesec_pocetak - 1, absence.dan_pocetak);
        const endAbsence = (absence.dan_kraj != null && absence.mesec_kraj != null && absence.godina_kraj != null)
            ? new Date(absence.godina_kraj, absence.mesec_kraj - 1, absence.dan_kraj)
            : startAbsence;

        // Upisi u cache odgovarajuceg meseca ako taj key vec postoji.
        const absenceMonth = absence.mesec_pocetak - 1;
        const absenceYear = absence.godina_pocetak;
        const absenceKey = getMonthKey(absenceMonth, absenceYear);
        const cachedMonth = statsMonthlyCache?.[absenceKey];

        if (cachedMonth?.calendarLoaded && Array.isArray(cachedMonth.calendarData)) {
            const alreadyInCache = cachedMonth.calendarData.some((item) => item._id === absence._id);
            if (!alreadyInCache) {
                get().setStatsMonthlyCache(absenceMonth, absenceYear, {
                    calendarData: [...cachedMonth.calendarData, absence],
                    calendarLoaded: true,
                    lastFetchedAt: Date.now()
                });
            }
        }

        if (!firstDisplayedDate || !lastDisplayedDate || !Array.isArray(calendarData)) return;
        const firstDisplayed = new Date(firstDisplayedDate);
        const lastDisplayed = new Date(lastDisplayedDate);

        const overlaps = startAbsence <= lastDisplayed && endAbsence >= firstDisplayed;
        if (overlaps) {
            const alreadyExists = calendarData.some((item) => item._id === absence._id);
            if (alreadyExists) return;

            const updatedCalendarData = [...calendarData, absence];
            set({ calendarData: updatedCalendarData });

            get().setStatsMonthlyCache(month, year, {
                calendarData: updatedCalendarData,
                calendarLoaded: true,
                lastFetchedAt: Date.now()
            });
        }
    },

    // Preko socketa
    handleAbsenceConfirm: (absence) => {
        set((state) => {
            if (!state.calendarData) return state;

            const updatedCalendarData = state.calendarData.map((item) => {
                if (item._id === absence._id) {
                    return {
                        ...item,
                        potvrda: absence.potvrda,
                        potvrdio_user_id: absence.potvrdio_user_id,
                    };
                }
                return item;
            });

            const updatedStatsMonthlyCache = Object.fromEntries(
                Object.entries(state.statsMonthlyCache || {}).map(([key, cacheEntry]) => {
                    if (!Array.isArray(cacheEntry?.calendarData)) return [key, cacheEntry];

                    const nextCalendarData = cacheEntry.calendarData.map((item) => {
                        if (item._id === absence._id) {
                            return {
                                ...item,
                                potvrda: absence.potvrda,
                                potvrdio_user_id: absence.potvrdio_user_id,
                            };
                        }
                        return item;
                    });

                    return [key, { ...cacheEntry, calendarData: nextCalendarData }];
                })
            );

            return {
                calendarData: updatedCalendarData,
                statsMonthlyCache: updatedStatsMonthlyCache
            };
        });
    },

    // Preko socketa
    handleAbsenceReject: (absence, shouldKeepRejected) => {
        set((state) => {
            if (!state.calendarData) return state;

            const selectedCalendarRequestId = state.selectedCalendarRequestId === absence._id
                ? null
                : state.selectedCalendarRequestId;

            if (!shouldKeepRejected) {
                const updatedStatsMonthlyCache = Object.fromEntries(
                    Object.entries(state.statsMonthlyCache || {}).map(([key, cacheEntry]) => {
                        if (!Array.isArray(cacheEntry?.calendarData)) return [key, cacheEntry];

                        return [key, {
                            ...cacheEntry,
                            calendarData: cacheEntry.calendarData.filter((item) => item._id !== absence._id)
                        }];
                    })
                );

                return {
                    calendarData: state.calendarData.filter((item) => item._id !== absence._id),
                    selectedCalendarRequestId,
                    statsMonthlyCache: updatedStatsMonthlyCache
                };
            }

            const updatedCalendarData = state.calendarData.map((item) => {
                if (item._id === absence._id) {
                    return {
                        ...item,
                        ...absence,
                        odbijen: true,
                    };
                }
                return item;
            });

            const updatedStatsMonthlyCache = Object.fromEntries(
                Object.entries(state.statsMonthlyCache || {}).map(([key, cacheEntry]) => {
                    if (!Array.isArray(cacheEntry?.calendarData)) return [key, cacheEntry];

                    const nextCalendarData = cacheEntry.calendarData.map((item) => {
                        if (item._id === absence._id) {
                            return {
                                ...item,
                                ...absence,
                                odbijen: true,
                            };
                        }
                        return item;
                    });

                    return [key, { ...cacheEntry, calendarData: nextCalendarData }];
                })
            );

            return {
                calendarData: updatedCalendarData,
                selectedCalendarRequestId,
                statsMonthlyCache: updatedStatsMonthlyCache
            };
        });
    },

    // Preko socketa
    handleAbsenceDelete: (absenceId) => {
        set((state) => ({
            calendarData: state.calendarData
                ? state.calendarData.filter((item) => item._id !== absenceId)
                : state.calendarData
        }));
    },

    handleEmployeeDelete: (userId) => {
        set((state) => {
            const userKey = Object.keys(state.employeesData || {}).find(
                (id) => String(id) === String(userId)
            );

            if (!userKey || !state.employeesData?.[userKey]) {
                return state;
            }

            const employee = state.employeesData[userKey];
            const empState = state.employeesState?.[userKey];
            const groupName = normalizeEmployeeGroupName(employee.group);

            const updatedEmployeesData = { ...state.employeesData };
            delete updatedEmployeesData[userKey];

            const updatedEmployeesState = { ...state.employeesState };
            delete updatedEmployeesState[userKey];

            const updatedPresenceNotifications = { ...state.presenceNotifications };
            delete updatedPresenceNotifications[userKey];

            const updatedGroupsState = { ...state.groupsState };
            if (updatedGroupsState[groupName]) {
                const group = updatedGroupsState[groupName];
                updatedGroupsState[groupName] = {
                    ...group,
                    allCards: Math.max(0, group.allCards - 1),
                    visibleCards: empState?.visibility
                        ? Math.max(0, group.visibleCards - 1)
                        : group.visibleCards,
                    currentActiveCards: empState?.currentActive
                        ? Math.max(0, group.currentActiveCards - 1)
                        : group.currentActiveCards,
                };
            }

            let updatedDevicesData = state.devicesData;
            if (state.devicesData) {
                const devicesData = { ...state.devicesData };
                let devicesChanged = false;

                for (const [clientKey, device] of Object.entries(devicesData)) {
                    if (String(device.employee) === userKey) {
                        devicesData[clientKey] = { ...device, employee: null };
                        devicesChanged = true;
                    }
                }

                if (devicesChanged) {
                    updatedDevicesData = devicesData;
                }
            }

            const updates = {
                employeesData: updatedEmployeesData,
                employeesState: updatedEmployeesState,
                groupsState: updatedGroupsState,
                presenceNotifications: updatedPresenceNotifications,
            };

            if (updatedDevicesData !== state.devicesData) {
                updates.devicesData = updatedDevicesData;
            }

            if (String(state.employeeMonthLogsModalUserId) === userKey) {
                updates.employeeMonthLogsModalUserId = null;
                updates.employeeMonthLogsByDay = null;
            }

            return updates;
        });
    },

    handleEmployeeUpdate: (updatedEmployee) => {
        set((state) => {
            const { userId, ime, prezime, username, tagid, group: newGroup, image, pendingAccountSetup } = updatedEmployee;

            const userKey = Object.keys(state.employeesData || {}).find(
                (id) => String(id) === String(userId)
            );

            if (!userKey || !state.employeesData?.[userKey]) {
                return state;
            }

            const employee = state.employeesData[userKey];
            const empState = state.employeesState?.[userKey] ?? {};
            const oldGroup = employee.group;
            const visibility = empState.visibility ?? false;
            const currentActive = empState.currentActive ?? false;

            const normalizeEmployeeGroup = normalizeEmployeeGroupName;

            const groupChanged = normalizeEmployeeGroup(oldGroup) !== normalizeEmployeeGroup(newGroup);
            const userKeyStr = String(userKey);
            const karnetPatch = {
                ime,
                prezime,
                group: newGroup,
            };

            const updatedEmployeesData = {
                ...state.employeesData,
                [userKey]: {
                    ...employee,
                    ime,
                    prezime,
                    username,
                    tagid,
                    group: newGroup,
                    image: image ?? employee.image ?? '',
                    ...(pendingAccountSetup !== undefined ? { pendingAccountSetup } : {}),
                },
            };

            let nextKarnetData = state.karnetData;
            let nextFilteredKarnetData = state.filteredKarnetData;

            if (nextKarnetData?.[userKeyStr]) {
                nextKarnetData = patchKarnetUserFields(nextKarnetData, userKeyStr, karnetPatch);
            }

            if (nextFilteredKarnetData?.[userKeyStr]) {
                nextFilteredKarnetData = patchKarnetUserFields(nextFilteredKarnetData, userKeyStr, karnetPatch);
            }

            const nextStatsMonthlyCache = patchStatsMonthlyCacheForEmployee(
                state.statsMonthlyCache,
                userKeyStr,
                karnetPatch,
            );

            let updatedGroupsState = { ...state.groupsState };
            let updatedDeviceGroupsState = state.deviceGroupsState
                ? { ...state.deviceGroupsState }
                : state.deviceGroupsState;

            if (groupChanged) {
                const oldKey = normalizeEmployeeGroup(oldGroup);
                const newKey = normalizeEmployeeGroup(newGroup);

                if (updatedGroupsState[oldKey]) {
                    updatedGroupsState[oldKey] = {
                        ...updatedGroupsState[oldKey],
                        allCards: Math.max(0, updatedGroupsState[oldKey].allCards - 1),
                        visibleCards: visibility
                            ? Math.max(0, updatedGroupsState[oldKey].visibleCards - 1)
                            : updatedGroupsState[oldKey].visibleCards,
                        currentActiveCards: currentActive
                            ? Math.max(0, updatedGroupsState[oldKey].currentActiveCards - 1)
                            : updatedGroupsState[oldKey].currentActiveCards,
                    };
                }

                if (!updatedGroupsState[newKey]) {
                    updatedGroupsState[newKey] = {
                        allCards: 0,
                        visibleCards: 0,
                        currentActiveCards: 0,
                    };
                }

                updatedGroupsState[newKey] = {
                    ...updatedGroupsState[newKey],
                    allCards: updatedGroupsState[newKey].allCards + 1,
                    visibleCards: updatedGroupsState[newKey].visibleCards + (visibility ? 1 : 0),
                    currentActiveCards: updatedGroupsState[newKey].currentActiveCards + (currentActive ? 1 : 0),
                };

                if (updatedDeviceGroupsState && !updatedDeviceGroupsState[newKey]) {
                    updatedDeviceGroupsState[newKey] = { allCadrs: 0, visibleCards: 0 };
                }
            }

            const updates = {
                employeesData: updatedEmployeesData,
                groupsState: updatedGroupsState,
            };

            if (nextKarnetData !== state.karnetData) {
                updates.karnetData = nextKarnetData;
            }

            if (nextFilteredKarnetData !== state.filteredKarnetData) {
                updates.filteredKarnetData = nextFilteredKarnetData;
            }

            if (nextStatsMonthlyCache !== state.statsMonthlyCache) {
                updates.statsMonthlyCache = nextStatsMonthlyCache;
            }

            if (groupChanged) {
                const userIdKey = String(userKey);
                updates.employeeGroupMovedUserIds = state.employeeGroupMovedUserIds.includes(userIdKey)
                    ? state.employeeGroupMovedUserIds
                    : [...state.employeeGroupMovedUserIds, userIdKey];
            }

            if (updatedDeviceGroupsState !== state.deviceGroupsState) {
                updates.deviceGroupsState = updatedDeviceGroupsState;
            }

            return updates;
        });
    },

    handleEmployeeCreate: (newEmployee) => {
        set((state) => {
            const {
                userId,
                ime,
                prezime,
                username,
                tagid,
                group,
                image,
                aktivan_status,
                presenceType,
                pendingAccountSetup,
            } = newEmployee;

            const userKey = String(userId);

            if (!state.employeesData || state.employeesData[userKey]) {
                return state;
            }

            const groupName = normalizeEmployeeGroupName(group);
            const presenceVisibility = state.presenceVisibility;
            const currentActive = aktivan_status === 'ulaz';
            const visibility = presenceVisibility === 'allEmployees'
                ? true
                : presenceVisibility === 'todayActive'
                    ? false
                    : currentActive;

            const updatedEmployeesData = {
                ...state.employeesData,
                [userKey]: {
                    user_id: userKey,
                    ime,
                    prezime,
                    username,
                    tagid: tagid || '',
                    group: group ?? null,
                    image: image || '',
                    aktivan_status: aktivan_status || 'izlaz',
                    presenceType: presenceType ?? null,
                    pendingAccountSetup: pendingAccountSetup ?? false,
                    presenceLogs: {},
                    hr: false,
                },
            };

            const updatedEmployeesState = {
                ...state.employeesState,
                [userKey]: {
                    currentActive,
                    wasActiveToday: false,
                    visibility,
                },
            };

            const updatedPresenceNotifications = {
                ...state.presenceNotifications,
                [userKey]: false,
            };

            let updatedGroupsState = { ...state.groupsState };
            if (!updatedGroupsState[groupName]) {
                updatedGroupsState[groupName] = {
                    allCards: 0,
                    visibleCards: 0,
                    currentActiveCards: 0,
                };
            }

            updatedGroupsState[groupName] = {
                ...updatedGroupsState[groupName],
                allCards: updatedGroupsState[groupName].allCards + 1,
                visibleCards: updatedGroupsState[groupName].visibleCards + (visibility ? 1 : 0),
                currentActiveCards: updatedGroupsState[groupName].currentActiveCards + (currentActive ? 1 : 0),
            };

            let updatedDeviceGroupsState = state.deviceGroupsState
                ? { ...state.deviceGroupsState }
                : state.deviceGroupsState;

            if (updatedDeviceGroupsState && groupName && !updatedDeviceGroupsState[groupName]) {
                updatedDeviceGroupsState[groupName] = { allCadrs: 0, visibleCards: 0 };
            }

            const updates = {
                employeesData: updatedEmployeesData,
                employeesState: updatedEmployeesState,
                groupsState: updatedGroupsState,
                presenceNotifications: updatedPresenceNotifications,
            };

            if (updatedDeviceGroupsState !== state.deviceGroupsState) {
                updates.deviceGroupsState = updatedDeviceGroupsState;
            }

            return updates;
        });
    },

    submitAbsenceRequest: async (payload) => {
        const { dateFrom, dateTo, absenceType, userId } = payload;
        const body = {
            absenceType,
            dan_pocetak: dateFrom.getDate(),
            mesec_pocetak: dateFrom.getMonth() + 1,
            godina_pocetak: dateFrom.getFullYear(),
            userId,
            ...(dateTo != null
                ? {
                    dan_kraj: dateTo.getDate(),
                    mesec_kraj: dateTo.getMonth() + 1,
                    godina_kraj: dateTo.getFullYear(),
                }
                : { dan_kraj: null, mesec_kraj: null, godina_kraj: null }),
        };
        const data = await apiPost(`${API_URL}/api/app/absenceRequest`, body);

        if (data?.ok) {
            set({ absenceRequestSuccessToast: "podnošenje" });
        }
    },

    updateAbsenceRequest: async (payload) => {
        const { zahtev_id, dateFrom, dateTo, absenceType, user_id } = payload;
        const body = {
            zahtev_id,
            absenceType,
            dan_pocetak: dateFrom.getDate(),
            mesec_pocetak: dateFrom.getMonth() + 1,
            godina_pocetak: dateFrom.getFullYear(),
            ...(dateTo != null
                ? {
                    dan_kraj: dateTo.getDate(),
                    mesec_kraj: dateTo.getMonth() + 1,
                    godina_kraj: dateTo.getFullYear(),
                }
                : { dan_kraj: null, mesec_kraj: null, godina_kraj: null }),
            ...(user_id ? { user_id } : {}),
        };
        const data = await apiPost(`${API_URL}/api/app/updateAbsenceRequest`, body);

        if (data?.ok) {
            set({ absenceRequestSuccessToast: "izmena" });
        }
    },

    deleteAbsenceRequest: async (payload) => {
        const { zahtev_id } = payload;

        const data = await apiPost(`${API_URL}/api/app/deleteAbsenceRequest`, {
            zahtev_id: zahtev_id
        });

        if (data?.ok) {
            set((state) => ({
                calendarData: state.calendarData
                    ? state.calendarData.filter((item) => item._id !== zahtev_id)
                    : state.calendarData
            }));
        }
    },

    confirmAbsenceRequest: async (payload) => {
        const { zahtev_id } = payload;

        const data = await apiPost(`${API_URL}/api/app/confirmAbsenceRequest`, {
            zahtev_id: zahtev_id
        });

        if (data?.ok) {
            set({ absenceRequestSuccessToast: "potvrda" });
        }
    },

    rejectAbsenceRequest: async (payload) => {
        const { zahtev_id } = payload;

        const data = await apiPost(`${API_URL}/api/app/rejectAbsenceRequest`, {
            zahtev_id: zahtev_id
        });

        if (data?.ok) {
            return true;
        }

        return false;
    },

    setKarnetRoundingInterval: (interval) => {
        set({
            karnetRoundingInterval: interval
        })
    },

    loadEmployeeMonthLogsData: async (userId, month, year) => {
        set({ loadingEmployeeMonthLogs: true, employeeMonthLogsByDay: null });
        try {
            const data = await apiPost(`${API_URL}/api/app/admin/employeeMonthLogs`, {
                userId,
                month: month + 1,
                year
            });
            set({
                employeeMonthLogsByDay: data ?? null,
                loadingEmployeeMonthLogs: false
            });
        } catch (error) {
            console.error('Error fetching employee month logs:', error);
            set({ employeeMonthLogsByDay: null, loadingEmployeeMonthLogs: false });
        }
    },

    openEmployeeMonthLogsModal: async (userId) => {
        const { month, year } = get().statsDateFilter;
        set({
            employeeMonthLogsModalUserId: userId,
            employeeMonthLogsModalDateFilter: { month, year }
        });
        await get().loadEmployeeMonthLogsData(userId, month, year);
    },

    showStatsMonthReport: async () => {
        const { month, year } = get().statsDateFilter;
        set({
            statsMonthReportModalOpen: true,
            loadingStatsMonthReport: true,
            statsMonthReportData: null,
            statsMonthReportDateFilter: { month, year }
        });

        try {
            const data = await apiPost(`${API_URL}/api/app/admin/showStatsMonthReport`, {
                month: month + 1,
                year
            });
            set({
                statsMonthReportData: data ?? null,
                loadingStatsMonthReport: false
            });
        } catch (error) {
            console.error('Error showing stats month report:', error);
            set({
                statsMonthReportModalOpen: false,
                loadingStatsMonthReport: false,
                statsMonthReportData: null
            });
        }
    },

    closeStatsMonthReportModal: () => {
        set({
            statsMonthReportModalOpen: false,
            loadingStatsMonthReport: false,
            statsMonthReportData: null
        });
    },

    setEmployeeMonthLogsModalDateMonth: async (month) => {
        const userId = get().employeeMonthLogsModalUserId;
        if (!userId) return;
        const year = get().employeeMonthLogsModalDateFilter.year;
        set((state) => ({
            employeeMonthLogsModalDateFilter: {
                ...state.employeeMonthLogsModalDateFilter,
                month
            }
        }));
        await get().loadEmployeeMonthLogsData(userId, month, year);
    },

    setEmployeeMonthLogsModalDateYear: async (year) => {
        const userId = get().employeeMonthLogsModalUserId;
        if (!userId) return;
        const month = get().employeeMonthLogsModalDateFilter.month;
        set((state) => ({
            employeeMonthLogsModalDateFilter: {
                ...state.employeeMonthLogsModalDateFilter,
                year
            }
        }));
        await get().loadEmployeeMonthLogsData(userId, month, year);
    },

    closeEmployeeMonthLogsModal: () => {
        set({
            employeeMonthLogsModalUserId: null,
            employeeMonthLogsByDay: null,
            loadingEmployeeMonthLogs: false
        });
    },

    updateEmployeeDayLogTime: async ({ logId, newTime }) => {
        try {
            const data = await apiPost(`${API_URL}/api/app/admin/updateEmployeeDayLogTime`, {
                logId,
                newTime
            });

            if (data?.ok) {
                set({ employeeDayLogTimeUpdateSuccessToast: true });
            }

            return data;
        } catch (error) {
            console.error('Error updating employee day log time:', error);
        }
    },

    handleEmployeeDayLogTimeUpdate: (payload) => {
        const { userId, logId, day, month, year, newTime, workedMinutes } = payload ?? {};
        if (
            !userId ||
            !logId ||
            day == null ||
            month == null ||
            year == null ||
            !newTime ||
            workedMinutes == null
        ) {
            return;
        }

        const month0 = Number(month) - 1;
        const yearNum = Number(year);
        const dayKey = String(day);
        const userKey = String(userId);

        const state = get();
        const updates = {};

        if (
            state.employeeMonthLogsModalUserId &&
            String(state.employeeMonthLogsModalUserId) === userKey &&
            state.employeeMonthLogsByDay &&
            state.employeeMonthLogsModalDateFilter?.month === month0 &&
            Number(state.employeeMonthLogsModalDateFilter?.year) === yearNum
        ) {
            updates.employeeMonthLogsByDay = patchEmployeeMonthLogsByDay(
                state.employeeMonthLogsByDay,
                payload
            );
        }

        const karnetViewMatches = monthFilterMatchesPayload(state.karnetFilter, month0, yearNum);
        const statsViewMatches = monthFilterMatchesPayload(state.statsDateFilter, month0, yearNum);
        const liveKarnetViewMatches = karnetViewMatches || statsViewMatches;

        if (state.karnetData && liveKarnetViewMatches) {
            const patched = patchKarnetUserDay(
                state.karnetData,
                state.filteredKarnetData,
                userKey,
                dayKey,
                workedMinutes
            );
            if (patched) {
                updates.karnetData = patched.karnetData;
                updates.filteredKarnetData = patched.filteredKarnetData;
                if (karnetViewMatches) {
                    updates.karnetRowFlashUserId = userKey;
                    updates.karnetRowFlashTick = (state.karnetRowFlashTick ?? 0) + 1;
                }
                if (statsViewMatches) {
                    updates.statsRowFlashUserId = userKey;
                    updates.statsRowFlashTick = (state.statsRowFlashTick ?? 0) + 1;
                }
            }
        }

        const cacheKey = getMonthKey(month0, yearNum);
        const cachedMonth = state.statsMonthlyCache?.[cacheKey];
        if (cachedMonth?.karnetLoaded && cachedMonth?.karnetData) {
            const patchedCache = patchKarnetUserDay(
                cachedMonth.karnetData,
                cachedMonth.filteredKarnetData,
                userKey,
                dayKey,
                workedMinutes
            );
            if (patchedCache) {
                updates.statsMonthlyCache = {
                    ...state.statsMonthlyCache,
                    [cacheKey]: {
                        ...cachedMonth,
                        karnetData: patchedCache.karnetData,
                        filteredKarnetData: patchedCache.filteredKarnetData
                    }
                };
            }
        }

        if (Object.keys(updates).length > 0) {
            set(updates);
        }
    },

    setKarnetData: async (month, year) => {
        try {
            const key = getMonthKey(month, year);
            const cachedMonth = get().statsMonthlyCache[key];
            const newFilter = {
                year: year,
                month: month,
            };

            set({
                karnetFilter: newFilter,
                loadingKarnetData: true
            });

            if (cachedMonth?.karnetLoaded && cachedMonth?.karnetData && cachedMonth?.filteredKarnetData) {
                set({
                    karnetFilter: newFilter,
                    karnetData: cachedMonth.karnetData,
                    filteredKarnetData: cachedMonth.filteredKarnetData,
                    loadingKarnetData: false
                });
                return;
            }

            const data = await apiPost(`${API_URL}/api/app/admin/monthReport`, {
                month: month + 1,
                year
            });

            const rawData = data;

            const filtered = Object.fromEntries(
                Object.entries(rawData).map(([id, user]) => {
                    const newDays = {}

                    Object.entries(user.days).forEach(([day, minutes]) => {
                        const h = Math.floor(minutes / 60)
                        const m = minutes % 60

                        // normalno formatiranje
                        const formatted = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`

                        // round na 30min: pretvaramo u sate i zaokružujemo na 0.5
                        const round30min = (Math.round(minutes / 30) * 30) / 60;

                        // round na 1h: zaokružujemo minute na sat (60 minuta) i delimo sa 60 da dobijemo sate
                        const round1h = Math.round(minutes / 60);

                        newDays[day] = {
                            formatted,
                            round30min,
                            round1h
                        }
                    })

                    return [id, {
                        ...user,
                        days: newDays
                    }]
                })
            )

            set({
                karnetFilter: newFilter,
                karnetData: rawData,
                filteredKarnetData: filtered,
                loadingKarnetData: false
            });

            get().setStatsMonthlyCache(month, year, {
                karnetData: rawData,
                filteredKarnetData: filtered,
                karnetLoaded: true,
                lastFetchedAt: Date.now()
            });
        } catch (err) {
            console.error("Error fetching karnet data:", err);
            set({ loadingKarnetData: false });
        }
    },

    toggleSidebar: () => set((state) => ({ isSidebarExpanded: !state.isSidebarExpanded })),

    closeSidebar: () => {
        set({ isSidebarExpanded: false })
    },

    setActiveSection: (section) => {
        set({ activeSection: section, isSidebarExpanded: false });

        const { statsDateFilter, userStatsDateFilter, karnetFilter } = get();
        if (section === "Statistika") {
            if (useAuthStore.getState().userRole === "admin") {
                void get().ensureStatsMonthData(statsDateFilter.month, statsDateFilter.year);
            } else {
                void get().ensureUserStatsMonthData(userStatsDateFilter.month, userStatsDateFilter.year);
            }
        } else if (section === "Karnet-obrazac") {
            void get().setKarnetData(karnetFilter.month, karnetFilter.year);
        } else if (section === "Kalendar") {
            void get().setCalendarData(
                get().calendarFilter.month,
                get().calendarFilter.year
            );
        }
    },

    toggleDevicePower: async (deviceClient, powerState) => {
        try {
            const data = await apiPost(`${API_URL}/api/app/toggleDevicePower`, {
                deviceClient,
                powerState
            });
            return data;
        } catch (error) {
            console.error('Error toggling device power:', error);
            // const errorMessage = error.response?.data?.error || error.message || 'Failed to toggle device power';
            return false;
        }
    },

    initializeApp: async () => {
        try {
            const data = await apiGet(`${API_URL}/api/app/init`);
            console.log(data);

            const userLogs = data.userLogs;
            const userNotificationRequests = data.userNotificationRequests;
            const notifications = data.notifications || [];

            const employeesState = {}
            const presenceNotifications = {}
            const groupsState = {}

            // Izračunaj broj nepročitanih obaveštenja
            const unreadNotificationCount = notifications.filter(n => !n.read).length;

            Object.keys(userLogs).forEach(key => {
                const currentActive = userLogs[key].aktivan_status === 'ulaz' ? true : false;
                const wasActiveToday = Object.keys(userLogs[key].presenceLogs).length > 0;

                const visibility = currentActive ? true : false

                employeesState[key] = {
                    currentActive: currentActive,
                    wasActiveToday: wasActiveToday,
                    visibility: visibility,
                }

                // Inicijalizujte presenceNotifications za svakog zaposlenog
                presenceNotifications[key] = false;

                const groupName = normalizeEmployeeGroupName(userLogs[key].group);
                if (!groupsState[groupName]) {
                    groupsState[groupName] = { allCards: 0, visibleCards: 0, currentActiveCards: 0 };
                }
                groupsState[groupName].allCards += 1;
                if (currentActive) {
                    groupsState[groupName].visibleCards += 1;
                    groupsState[groupName].currentActiveCards += 1;
                }
            });

            userNotificationRequests.forEach(key => {
                if (presenceNotifications.hasOwnProperty(key)) {
                    presenceNotifications[key] = true;
                }
            });

            connectSocket();

            set({
                employeesData: userLogs,
                employeesState: employeesState,
                groupsState: groupsState,
                hasHR: !!data.hasHR,
                presenceNotifications: presenceNotifications,
                notifications: notifications,
                unreadNotificationCount: unreadNotificationCount,
                loading: false,
                error: null
            });

        } catch (error) {
            set({ error: error.response?.data?.error || 'first api call failed' });
            return false;
        }
    },

    setPresenceVisibility: (visibility) => {
        const state = get();
        if (visibility === state.presenceVisibility) return
        const updatedEmployeesState = { ...state.employeesState };

        // Prikaz svih zaposlenih
        if (visibility === 'allEmployees') {
            for (const [id, empState] of Object.entries(state.employeesState)) {
                if (!empState.visibility) {
                    updatedEmployeesState[id] = {
                        ...empState,
                        visibility: true
                    };
                }
            }
        }
        // Prikaz samo trenutno aktivnih zaposlenih
        else if (visibility === 'currentActive') {
            for (const [id, empState] of Object.entries(state.employeesState)) {
                if (empState.visibility !== empState.currentActive) {
                    updatedEmployeesState[id] = {
                        ...empState,
                        visibility: empState.currentActive
                    };
                }
            }
        }
        // Prikaz samo zaposlenih koji su bili aktivni danas
        else if (visibility === 'todayActive') {
            for (const [id, empState] of Object.entries(state.employeesState)) {
                // if (!empState.currentActive && empState.wasActiveToday) {
                if (empState.wasActiveToday) {
                    updatedEmployeesState[id] = {
                        ...empState,
                        visibility: true
                    };
                } else {
                    updatedEmployeesState[id] = {
                        ...empState,
                        visibility: false
                    };
                }
            }

        }

        const groupsState = { ...state.groupsState };
        const newGroupsState = {};

        // broji aktivne kartice po grupi
        for (const [id, empState] of Object.entries(updatedEmployeesState)) {
            if (!empState.visibility) continue;

            const groupName = normalizeEmployeeGroupName(state.employeesData[id].group);

            if (!newGroupsState[groupName]) {
                newGroupsState[groupName] = 1;
            } else {
                newGroupsState[groupName]++;
            }
        }

        const finalGroupsState = {};
        for (const [groupName, groupData] of Object.entries(groupsState)) {
            finalGroupsState[groupName] = {
                allCards: groupData.allCards, // originalna veličina
                visibleCards: newGroupsState[groupName] || 0, // ako nema u novom, znači 0
                currentActiveCards: groupData.currentActiveCards
            };
        }

        set({
            presenceVisibility: visibility,
            employeesState: updatedEmployeesState,
            groupsState: finalGroupsState
        });
    },

    setPresenceLog: (userId, presenceLog, newStatus, presenceType) => {
        const state = get();
        const presenceVisibility = state.presenceVisibility;

        const updatedEmployeesData = {
            ...state.employeesData,
            [userId]: {
                ...state.employeesData[userId],
                aktivan_status: newStatus,
                presenceLogs: presenceLog,
                presenceType: presenceType
            }
        }

        const updatedEmployeesState = {
            ...state.employeesState,
            [userId]: {
                currentActive: newStatus === 'ulaz',
                wasActiveToday: true,
                visibility: true
            }
        }

        if (presenceVisibility === 'currentActive') {
            const visibility = updatedEmployeesState[userId].currentActive;
            updatedEmployeesState[userId].visibility = visibility
        }

        const groupName = normalizeEmployeeGroupName(updatedEmployeesData[userId].group);

        const updatedGroupsState = calculateEmployeeCardsInGroups({
            groupName,
            presenceVisibility,
            updatedEmployeesData,
            updatedEmployeesState,
            groupsState: state.groupsState,
            prevEmployeesData: state.employeesData,
            userId
        });

        set({
            employeesData: updatedEmployeesData,
            employeesState: updatedEmployeesState,
            groupsState: updatedGroupsState
        });
    },

    // Prijava/Odjava preko fronta
    handleWorkCheckIn: async (checkInData = {}) => {
        try {
            const payload = await apiPost(`${API_URL}/api/presence/toggleShift`, checkInData)
            console.log(payload);
            if (payload?.success === true) {
                set({
                    workCheckInToast: {
                        type: "success",
                        title: "Evidencija uspešna",
                        message: payload?.message || "Prijava/odjava je uspešno evidentirana."
                    }
                });

                return { success: true, message: payload?.message || "OK" };
            }

            const serverMessage = payload?.message || (typeof payload === "string" ? payload : "Evidencija nije uspela.");
            set({
                workCheckInToast: {
                    type: "error",
                    title: "Evidencija nije uspela",
                    message: serverMessage
                }
            });
            return { success: false, message: serverMessage };
        } catch (err) {
            console.error('Error checkIn/checkOut', err)
            const fallbackMessage = err?.response?.data?.message || "Greška servera pri evidenciji.";
            set({
                workCheckInToast: {
                    type: "error",
                    title: "Evidencija nije uspela",
                    message: fallbackMessage
                }
            });
            return { success: false, message: fallbackMessage };
        }
    },

    // Za sad se koristi za auto-logout 00:00 employees
    syncPresenceLogs: (logArray) => {
        const state = get();
        const presenceVisibility = state.presenceVisibility;

        const updatedEmployeesData = { ...state.employeesData };
        const updatedEmployeesState = { ...state.employeesState };
        let updatedGroupsState = { ...state.groupsState };

        for (const { userId, presenceLogs, newStatus } of logArray) {
            updatedEmployeesData[userId] = {
                ...updatedEmployeesData[userId],
                aktivan_status: newStatus,
                presenceLogs: presenceLogs
            }

            const currentActive = newStatus === 'ulaz';

            updatedEmployeesState[userId] = {
                wasActiveToday: true,
                currentActive: currentActive,
                visibility: true
            }

            if (presenceVisibility === 'currentActive') {
                const visibility = updatedEmployeesState[userId].currentActive
                updatedEmployeesState[userId].visibility = visibility
            }

            const groupName = normalizeEmployeeGroupName(updatedEmployeesData[userId].group);
            updatedGroupsState = calculateEmployeeCardsInGroups({
                groupName,
                presenceVisibility,
                updatedEmployeesData,
                updatedEmployeesState,
                groupsState: updatedGroupsState,
                prevEmployeesData: state.employeesData,
                userId
            });
        }

        set({
            employeesData: updatedEmployeesData,
            employeesState: updatedEmployeesState,
            groupsState: updatedGroupsState
        })
    },

    // Koristi se za auto-logout za treću smenu sa kalkulacijom radnog vremena
    syncPresenceLogsWithCalculation: (usersToExit) => {
        const state = get();
        const presenceVisibility = state.presenceVisibility;

        const updatedEmployeesData = { ...state.employeesData };
        const updatedEmployeesState = { ...state.employeesState };
        let updatedGroupsState = { ...state.groupsState };

        for (const { userId: user_Id } of usersToExit) {
            const oldPresenceLogs = updatedEmployeesData[user_Id].presenceLogs;
            const newPresenceLogs = {
                ...oldPresenceLogs,
                expectedExit: null,
                exit: oldPresenceLogs.pauses[oldPresenceLogs.pauses.length - 1]?.kraj || oldPresenceLogs.entry
            };

            updatedEmployeesData[user_Id] = {
                ...updatedEmployeesData[user_Id],
                aktivan_status: 'izlaz',
                presenceLogs: newPresenceLogs
            };

            updatedEmployeesState[user_Id] = {
                wasActiveToday: true,
                currentActive: false,
                visibility: true
            }

            if (presenceVisibility === 'currentActive') {
                updatedEmployeesState[user_Id].visibility = false
            }

            const groupName = normalizeEmployeeGroupName(updatedEmployeesData[user_Id].group);
            updatedGroupsState = calculateEmployeeCardsInGroups({
                groupName,
                presenceVisibility,
                updatedEmployeesData,
                updatedEmployeesState,
                groupsState: updatedGroupsState,
                prevEmployeesData: state.employeesData,
                userId: user_Id
            });
        }

        set({
            employeesData: updatedEmployeesData,
            employeesState: updatedEmployeesState,
            groupsState: updatedGroupsState
        })
    },

    setPresenceNotification: async (employeeId) => {
        try {
            const data = await apiPost(`${API_URL}/api/presence/email-notifications`, {
                employeeId: employeeId
            });

            // Server treba da vrati novo stanje u odgovoru
            const newStatus = data.subscribed; // true/false

            set((state) => ({
                presenceNotifications: {
                    ...state.presenceNotifications,
                    [employeeId]: newStatus
                }
            }));
        } catch (error) {
            console.error('Error sending notification:', error);
        }
    },

    setDevicesData: async () => {
        try {
            const data = await apiGet(`${API_URL}/api/app/admin/getDevices`);

            const devicesState = { ...get().devicesState };
            const deviceGroupsState = {};

            const devicesData = {};
            const employeesData = get().employeesData;

            data.forEach(device => {

                devicesData[device.client] = { ...device }

                const group = device.group || 'Bez grupe';

                if (!deviceGroupsState[group]) {
                    deviceGroupsState[group] = { allCadrs: 0, visibleCards: 0 };
                }

                deviceGroupsState[group].allCadrs += 1;
                deviceGroupsState[group].visibleCards += 1;

                if (devicesState[device.client]) return;

                devicesState[device.client] = {
                    status: "waiting", // "waiting", "online", "offline"
                    lastPing: null,
                    visibility: true,
                    rssi: null,
                    deviceType: device.device
                };
            });

            if (employeesData) {
                Object.values(employeesData).forEach(employee => {
                    const groupName = employee?.group;
                    if (!groupName) return;
                    if (!deviceGroupsState[groupName]) {
                        deviceGroupsState[groupName] = { allCadrs: 0, visibleCards: 0 };
                    }
                });
            }

            set({
                devicesData: devicesData,
                devicesState: devicesState,
                deviceGroupsState: deviceGroupsState,
                devicesVisibility: 'allDevices',
                deviceDataSignal: true
            })
        } catch (error) {
            set({ error: error.response?.data?.error || 'Devices api call failed' });
            return false;
        }
    },

    setDevicesVisibility: (visibility) => {
        const state = get();
        if (visibility === state.devicesVisibility) return

        const updatedDevicesState = { ...state.devicesState };

        if (visibility === 'allDevices') {
            for (const [key, devState] of Object.entries(state.devicesState)) {
                if (!devState.visibility) {
                    updatedDevicesState[key] = {
                        ...devState,
                        visibility: true
                    };
                }
            }
        } else if (visibility === 'statusOn') {
            for (const [key, devState] of Object.entries(state.devicesState)) {
                updatedDevicesState[key] = {
                    ...devState,
                    visibility: devState.status === 'online'
                }
            }
        } else if (visibility === 'statusOff') {
            for (const [key, devState] of Object.entries(state.devicesState)) {
                updatedDevicesState[key] = {
                    ...devState,
                    visibility: devState.status !== 'online'
                }
            }
        }

        const updatedDeviceGroupsState = { ...state.deviceGroupsState };
        const deviceData = { ...state.devicesData };

        const finalGroupsState = calculateVisibleDeviceCardsInGroups(deviceData, updatedDevicesState, updatedDeviceGroupsState);

        set({
            devicesVisibility: visibility,
            devicesState: updatedDevicesState,
            deviceGroupsState: finalGroupsState
        })
    },


    handleDeviceUpdate: (updatedDevice) => {
        const devicesData = { ...get().devicesData };
        const deviceGroupsState = { ...get().deviceGroupsState };

        const deviceEntry = Object.entries(devicesData).find(
            ([, device]) => device._id === updatedDevice.deviceId
        );

        if (!deviceEntry) return;

        const [clientKey, device] = deviceEntry;

        const visibility = get().devicesState[clientKey].visibility;
        const oldGroup = device.group;
        const newGroup = updatedDevice.group;

        if (oldGroup !== newGroup) {
            if (deviceGroupsState[oldGroup]) {
                deviceGroupsState[oldGroup] = {
                    allCadrs: deviceGroupsState[oldGroup].allCadrs - 1,
                    visibleCards: deviceGroupsState[oldGroup].visibleCards - (visibility ? 1 : 0)
                };
            }

            if (!deviceGroupsState[newGroup]) {
                deviceGroupsState[newGroup] = { allCadrs: 1, visibleCards: visibility ? 1 : 0 };
            } else {
                deviceGroupsState[newGroup] = {
                    allCadrs: deviceGroupsState[newGroup].allCadrs + 1,
                    visibleCards: deviceGroupsState[newGroup].visibleCards + (visibility ? 1 : 0)
                };
            }
        }

        devicesData[clientKey] = {
            ...device,
            room: updatedDevice.room,
            group: updatedDevice.group,
            employee: updatedDevice.employee
        };

        const deviceGroupMovedClients =
            oldGroup !== newGroup
                ? get().deviceGroupMovedClients.includes(clientKey)
                    ? get().deviceGroupMovedClients
                    : [...get().deviceGroupMovedClients, clientKey]
                : get().deviceGroupMovedClients;

        set({ devicesData, deviceGroupsState, deviceGroupMovedClients });
    },

    updateDeviceData: async (updatedDevice) => {
        try {
            const data = await apiPost(`${API_URL}/api/app/admin/updateDevice`, {
                deviceId: updatedDevice.deviceId,
                room: updatedDevice.room,
                employee: updatedDevice.employee,
                group: updatedDevice.group
            });

            if (data.success) {
                set({ deviceUpdateSuccessToast: true });
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating device data:', error);
            return false;
        }
    },

    deleteEmployee: async (userId) => {
        try {
            const data = await apiPost(`${API_URL}/api/app/admin/deleteEmployee`, {
                user_id: userId,
            });
            if (data?.success === true) {
                set({ employeeDeleteSuccessToast: true });
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error deleting employee:', error);
            return false;
        }
    },

    updateEmployee: async (payload) => {
        try {
            const { image, ...fields } = payload;
            let data;

            if (image) {
                const formData = new FormData();
                Object.entries(fields).forEach(([key, value]) => {
                    formData.append(key, value ?? '');
                });
                formData.append('image', image, 'employee.jpg');

                const response = await axios.post(
                    `${API_URL}/api/app/admin/updateEmployee`,
                    formData,
                    { withCredentials: true },
                );
                data = response.data;
            } else {
                data = await apiPost(`${API_URL}/api/app/admin/updateEmployee`, fields);
            }

            if (data?.success === true) {
                get().handleEmployeeUpdate({
                    userId: fields.user_id,
                    ime: fields.ime,
                    prezime: fields.prezime,
                    username: fields.username,
                    tagid: fields.tagid,
                    group: data.group,
                    image: data.image ?? '',
                });
                set({ employeeUpdateSuccessToast: true });
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error updating employee:', error);
            return error?.response?.data?.error || false;
        }
    },

    createEmployee: async (payload) => {
        try {
            const { image, ...fields } = payload;
            let data;

            if (image) {
                const formData = new FormData();
                Object.entries(fields).forEach(([key, value]) => {
                    formData.append(key, value ?? '');
                });
                formData.append('image', image, 'employee.jpg');

                const response = await axios.post(
                    `${API_URL}/api/app/admin/createEmployee`,
                    formData,
                    { withCredentials: true },
                );
                data = response.data;
            } else {
                data = await apiPost(`${API_URL}/api/app/admin/createEmployee`, fields);
            }

            if (data?.success === true) {
                set({ employeeCreateSuccessToast: true });
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error creating employee:', error);
            return error?.response?.data?.error || false;
        }
    },

    deviceStatusUpdate: (data) => {
        set((state) => {
            const updatedDevicesState = { ...state.devicesState };
            const devicesVisibility = state.devicesVisibility;
            const { client, rssi, time } = data;

            // if (!state.devicesData?.[client]) {
            // console.log('[deviceStatusUpdate] uređaj nije u bazi, client:', client);
            // return {};
            // }

            updatedDevicesState[client] = {
                ...updatedDevicesState[client],
                status: "online",
                lastPing: time,
                visibility: devicesVisibility !== "statusOff",
                rssi: rssi
            };

            if (data.power) {
                updatedDevicesState[client].power = data.power;
            }

            if (devicesVisibility === 'allDevices' || !state.devicesData) {
                return {
                    devicesState: {
                        ...state.devicesState,
                        [client]: updatedDevicesState[client]
                    }
                };
            } else {
                const lastStatus = state.devicesState[client].status;

                if (lastStatus === 'online') {
                    return {
                        devicesState: {
                            ...state.devicesState,
                            [client]: updatedDevicesState[client]
                        }
                    };
                } else {
                    const groupName = state.devicesData[client].group || 'Bez grupe';
                    const updatedDeviceGroupsState = { ...state.deviceGroupsState };

                    updatedDeviceGroupsState[groupName] = {
                        ...updatedDeviceGroupsState[groupName],
                        visibleCards: devicesVisibility === 'statusOn'
                            ? updatedDeviceGroupsState[groupName].visibleCards + 1
                            : updatedDeviceGroupsState[groupName].visibleCards - 1
                    };

                    return {
                        devicesState: {
                            ...state.devicesState,
                            [client]: updatedDevicesState[client]
                        },
                        deviceGroupsState: updatedDeviceGroupsState
                    };
                }
            }
        });
    },

    setDevicesNewStatus: ({ waitingDevices, onlineDevices }) => {
        set((state) => {
            const updatedDevicesState = { ...state.devicesState };
            const devicesVisibility = state.devicesVisibility

            // Obeleži waiting uređaje kao offline
            waitingDevices.forEach((deviceKey) => {
                if (updatedDevicesState[deviceKey]) {
                    updatedDevicesState[deviceKey] = {
                        ...updatedDevicesState[deviceKey],
                        status: "offline",
                        visibility: devicesVisibility !== "statusOn"
                    };
                }
            });

            const now = new Date();
            const currentTimeString = now.toTimeString().split(" ")[0];

            // Proveri online uređaje
            onlineDevices.forEach((deviceKey) => {
                const device = updatedDevicesState[deviceKey];
                if (device) {
                    const diffSeconds = getTimeDifferenceInSeconds(
                        currentTimeString,
                        device.lastPing
                    );

                    const threshold = ["TX1", "TX2", "S26R2"].includes(device.deviceType)
                        ? 10
                        : 30;

                    if (diffSeconds > threshold) {
                        updatedDevicesState[deviceKey] = {
                            ...device,
                            status: "waiting",
                            visibility: devicesVisibility !== "statusOff"
                        };
                    }
                }
            });

            if (devicesVisibility === 'allDevices') return { devicesState: updatedDevicesState };

            const deviceData = { ...state.devicesData };
            const updatedDeviceGroupsState = { ...state.deviceGroupsState };

            const finalGroupsState = calculateVisibleDeviceCardsInGroups(deviceData, updatedDevicesState, updatedDeviceGroupsState);

            return { devicesState: updatedDevicesState, deviceGroupsState: finalGroupsState };
        });
    },

    // Substring search - radi
    filterEmployeesBySearch: (searchValue) => {
        const state = get();
        const search = searchValue.toLowerCase();

        const updatedEmployeesState = { ...state.employeesState };

        for (const [id, empData] of Object.entries(state.employeesData)) {
            const fullName = `${empData.ime} ${empData.prezime}`.toLowerCase();
            updatedEmployeesState[id] = {
                ...updatedEmployeesState[id],
                visibility: fullName.includes(search)
            };
        }

        const updatedGroupsState = { ...state.groupsState };
        for (const [groupName, group] of Object.entries(updatedGroupsState)) {
            // Prebroj sve zaposlene iz te grupe koji imaju visibility = true
            const activeCardsCount = Object.entries(state.employeesData)
                .filter(([, empData]) => normalizeEmployeeGroupName(empData.group) === groupName)
                .filter(([empId]) => updatedEmployeesState[empId].visibility)
                .length;

            updatedGroupsState[groupName] = {
                ...group,
                visibleCards: activeCardsCount
            };
        }

        set({
            employeesState: updatedEmployeesState,
            groupsState: updatedGroupsState
        });
    },

    // Substring search
    filterDevicesBySearch: (searchValue) => {
        const state = get();
        const search = searchValue.toLowerCase();

        const updatedDevicesState = { ...state.devicesState };

        // Iteriraj kroz objekat devicesData
        Object.values(state.devicesData).forEach(device => {
            const match = (
                device.device.toLowerCase().includes(search) ||
                device.room.toLowerCase().includes(search) ||
                device.type.toLowerCase().includes(search)
            );

            updatedDevicesState[device.client] = {
                ...updatedDevicesState[device.client],
                visibility: match
            };
        });

        const updatedDeviceGroupsState = { ...state.deviceGroupsState };

        // Ažuriraj broj vidljivih kartica u grupama
        for (const [groupName, group] of Object.entries(updatedDeviceGroupsState)) {
            const visibleCardsCount = Object.values(state.devicesData)
                .filter((device) => device.group === groupName || (!device.group && groupName === "Bez grupe"))
                .filter((device) => updatedDevicesState[device.client].visibility)
                .length;

            updatedDeviceGroupsState[groupName] = {
                ...group,
                visibleCards: visibleCardsCount
            };
        }

        set({ devicesState: updatedDevicesState, deviceGroupsState: updatedDeviceGroupsState });
    },

    // Fuzzy-search, za sad ne radi, treba testirati
    filterEmployeesByFuzzySearch: (searchValue) => {
        const state = get();

        if (!searchValue || searchValue.trim() === '') {
            // Ako je prazno polje za pretragu, prikaži po trenutnoj visibility logici (npr. allEmployees)
            return;
        }

        const employeesArray = Object.entries(state.employeesData).map(([id, emp]) => ({
            id: id.toString(),
            ime: emp.ime,
            prezime: emp.prezime,
        }));


        const fuse = new Fuse(employeesArray, {
            keys: ['ime', 'prezime'],
            threshold: 0.1,  // prilagodi po želji, manja vrednost = stroža pretraga
            ignoreLocation: true,
        });
        const results = fuse.search(searchValue);

        const matchedIds = new Set(results.map(r => r.item.id));

        const updatedEmployeesState = { ...state.employeesState };

        // OVDE JE VEROVATNO PROBELM
        for (const id in updatedEmployeesState) {
            updatedEmployeesState[id].visibility = matchedIds.has(id);
        }

        set({ employeesState: updatedEmployeesState });
    },
}));


const getTimeDifferenceInSeconds = (time1, time2) => {
    const toSeconds = (timeStr) => {
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    };

    const sec1 = toSeconds(time1);
    const sec2 = toSeconds(time2);

    let diff = Math.abs(sec1 - sec2);

    // Ako je razlika veća od 12h, pretpostavimo da je prelazak dana
    if (diff > 43200) {
        diff = 86400 - diff;
    }

    return diff;
};

const calculateVisibleDeviceCardsInGroups = (deviceData, updatedDevicesState, updatedDeviceGroupsState) => {

    const newDeviceGroupsState = {};

    for (const [key, devState] of Object.entries(updatedDevicesState)) {
        if (!devState.visibility) continue;

        const groupname = deviceData[key].group || 'Bez grupe';

        if (!newDeviceGroupsState[groupname]) {
            newDeviceGroupsState[groupname] = 1;
        } else {
            newDeviceGroupsState[groupname]++;
        }
    }

    const finalGroupsState = {};
    for (const [groupName, groupData] of Object.entries(updatedDeviceGroupsState)) {
        finalGroupsState[groupName] = {
            allCadrs: groupData.allCadrs,
            visibleCards: newDeviceGroupsState[groupName] || 0,
        }
    }

    return finalGroupsState;
}

// helpers/presenceHelpers.js
export const calculateEmployeeCardsInGroups = ({
    groupName,
    presenceVisibility,
    updatedEmployeesData,
    updatedEmployeesState,
    groupsState,
    prevEmployeesData,
    userId
}) => {
    const updatedGroupsState = { ...groupsState };
    const groupKey = normalizeEmployeeGroupName(groupName);

    if (!updatedGroupsState[groupKey]) {
        updatedGroupsState[groupKey] = { allCards: 0, visibleCards: 0, currentActiveCards: 0 };
    }

    if (updatedEmployeesState[userId].currentActive) {
        const workedMinutes = prevEmployeesData[userId]?.presenceLogs?.workedMinutes;
        const visibleCards = updatedGroupsState[groupKey].visibleCards;

        switch (presenceVisibility) {
            case 'allEmployees':
                updatedGroupsState[groupKey] = {
                    ...updatedGroupsState[groupKey],
                    currentActiveCards: updatedGroupsState[groupKey].currentActiveCards + 1
                };
                break;
            case 'currentActive':
                updatedGroupsState[groupKey] = {
                    ...updatedGroupsState[groupKey],
                    currentActiveCards: updatedGroupsState[groupKey].currentActiveCards + 1,
                    visibleCards: visibleCards + 1
                };
                break;
            case 'todayActive':
                updatedGroupsState[groupKey] = {
                    ...updatedGroupsState[groupKey],
                    currentActiveCards: updatedGroupsState[groupKey].currentActiveCards + 1,
                    visibleCards: workedMinutes ? visibleCards : visibleCards + 1
                };
                break;
        }

    } else {
        const visibleCards = updatedGroupsState[groupKey].visibleCards;

        switch (presenceVisibility) {
            case 'allEmployees':
                updatedGroupsState[groupKey] = {
                    ...updatedGroupsState[groupKey],
                    currentActiveCards: updatedGroupsState[groupKey].currentActiveCards - 1
                };
                break;
            case 'currentActive':
                updatedGroupsState[groupKey] = {
                    ...updatedGroupsState[groupKey],
                    currentActiveCards: updatedGroupsState[groupKey].currentActiveCards - 1,
                    visibleCards: visibleCards - 1
                };
                break;
            case 'todayActive':
                updatedGroupsState[groupKey] = {
                    ...updatedGroupsState[groupKey],
                    currentActiveCards: updatedGroupsState[groupKey].currentActiveCards - 1,
                };
                break;
        }
    }

    return updatedGroupsState;
};
